import { DynamicSearchControl, SearchControlType } from "./dynamicSearch.js"
import { Button } from "../core/components/button.js";
import { Helper } from "../core/util/helper.js";
import { NotificationHelper } from "../core/util/notificationHelper.js";
import { HTTPService } from "../core/util/httpManager.js";
import { Config } from "../core/config/config.js";
import { StringKeyDictionary } from "../commonInterfaces.js";
import React from "react";
export default class SearchTemplates extends React.Component<DynamicSearchTemplatesProps, {
    autoSearch?: boolean;
    templateName?: string;
    merge: boolean;
}> {
    private _templatesElementRef = null;
    private _templatesRef: kendo.ui.DropDownList = null;
    //data source for the drop down list 
    private _templatesDS: Array<{ text: string; value: string; }> = [];
    private _noTemplateOptionId = "*n*o**t*";
    private _newTemplateOptionId = "*n*e*w**t*";
    private _currentTemplate = this._noTemplateOptionId;
    private _autoSearchLabelElementRef = null;
    //current db data
    private _storedTemplatesData: PersistedSearchTemplates = {
        data: [],
        cfg: {
            runOnSelect: true,
            currentTemplate: ""
        }
    };
    //hash for accessing the data...
    //private _storedTemplatesHash: StringKeyDictionary<StringKeyDictionary<SingleSearchElementData>> = {}
    //values from the filter inputs
    private _filterData: StringKeyDictionary<any> = null;
    //unique button ids
    ///////////////////////////////////////////
    private _removeOldData = false;//set to true in the next release
    private _enableSaveButton = false;
    private _enableDeleteButton = false;
    private _STORAGE_KEY = "SEARCH_TEMPLATE_DEFAULT";
    private _initialLoad = true;

    constructor(props: DynamicSearchTemplatesProps) {
        super(props);
        this._STORAGE_KEY += `_${props.searchType}_${Config.user.userName}`;
        //add "No Template" and "New Template" options
        this._templatesDS.push({
            text: props.translations["Suche.NoTemplate"],
            value: this._noTemplateOptionId
        });
        this._templatesDS.push({
            text: props.translations["Suche.NewTemplate"],
            value: this._newTemplateOptionId
        });

        this.state = {
            autoSearch: false,
            templateName: "",
            merge: false
        };
    }


    private _updateNewFormatData = (templates: Array<SingleSearchElementData>) => {
        if (Array.isArray(templates)) {
            const seStr = "SearchElements_";
            //create a hash for easier handling
            //id stored separately because it will be removed from the object
            const hash: StringKeyDictionary<Array<{ item: SingleSearchElementData; id: string; }>> = {};

            templates.forEach((t) => {
                if (!Helper.hasValue(t.colRef)) {
                    const idxStart = t.id.indexOf(seStr);
                    const idxEnd = t.id.lastIndexOf("_");
                    const colRef = t.id.substring(idxStart + seStr.length, idxEnd);

                    //get final column name (remove data source name, if set)
                    if (colRef.indexOf("_") === -1) {
                        t.colRef = colRef;
                        delete t.source;
                    } else {
                        const idx = colRef.indexOf("_");
                        t.colRef = colRef.substr(0, idx);
                        t.source = colRef.substr(idx + 1);
                    }

                    //add to hash (check later if the component is a "range" component
                    if (!hash[colRef]) {
                        hash[colRef] = [];
                    }

                    hash[colRef].push({ item: t, id: t.id });

                    //delete other (old, unused) properties
                    if (this._removeOldData) {
                        delete t.id;
                        delete t.type;
                    }

                    if (Helper.hasValue(t.val) && t.val === "") {
                        t.val = null;
                    }
                }
            });

            for (let colRef in hash) {
                //multiple entries for the same column => date(time) range
                if (hash[colRef].length > 1) {
                    //only 2 controls for now...
                    const item1 = hash[colRef][0];
                    const item2 = hash[colRef][1];
                    const idIdx1 = parseInt(item1.id.substr(item1.id.lastIndexOf("_") + 1), 10);
                    const idIdx2 = parseInt(item2.id.substr(item2.id.lastIndexOf("_") + 1), 10);

                    //update the column "name" to show which one represents the start and which the end
                    if (idIdx1 < idIdx2) {
                        item1.item.colRef += "_start";
                        item2.item.colRef += "_end";
                    } else {
                        item2.item.colRef += "_start";
                        item1.item.colRef += "_end";
                    }
                }
            }
        }
    }

    //toggle toolbar button "enabled" state
    private _changeButtonState = (forSave: boolean, on: boolean) => {
        if (forSave)
            this._enableSaveButton = on;
        else
            this._enableDeleteButton = on;

        this.setState({});
    }

    private _triggerTemplateChanged = (target: PersistedSearchTemplateInstance) => {
        const data = SearchTemplatesHelper.mapPersitedSearchTemplateInstanceToData(target);

        //enable buttons
        this._changeButtonState(true, true);
        this._changeButtonState(false, true);

        //trigger the change event and send the data
        if (!this._initialLoad) {
            this.props.onTemplateSelected(data, this.state.autoSearch, this.state.merge);
            Helper.saveDataToStorage(this._STORAGE_KEY, target, false);
        }
    }

    private _onTemplateChange = (value: string) => {
        this._currentTemplate = value;
        this.setState({ templateName: "" }, () => {
            if (value === this._noTemplateOptionId) {
                //disable buttons
                this._changeButtonState(true, false);
                this._changeButtonState(false, false);
                //no template => reset all the filter components
                this.props.onTemplateSelected(null, this.state.autoSearch, this.state.merge);
                Helper.removeDataFromStorage(this._STORAGE_KEY);
            } else if (value == this._newTemplateOptionId) {
                //enable save
                this._changeButtonState(true, true);
                //disable delete
                this._changeButtonState(false, false);
            } else {
                //get the values for the selected template
                let target: PersistedSearchTemplateInstance = this._storedTemplatesData.data.find(template => template.name === value);
                if (target) {
                    this._triggerTemplateChanged(target);
                }
            }
        });
    }

    private _onBtnClick = (isSave: boolean) => {
        if (isSave) {
            this._saveTemplate();
        } else {
            this._deleteTemplate();
        }
    }

    private _onNewNameChange = (value: string) => {
        this.setState({ templateName: value });
    }

    //get the available search templates
    //!!! should be called only once (componentDidMount)
    private _loadSearchTemplates = async () => {
        const result = await HTTPService.get<string>("DynamicSearch/GetSearchParameters?searchType=" + this.props.searchType);
        if (!Helper.isNullOrEmpty(result)) {
            this._storedTemplatesData = Helper.tryParseJSON(result);
            if (!Helper.hasValue(this._storedTemplatesData)) {
                this._storedTemplatesData = {
                    cfg: {
                        runOnSelect: false,
                        currentTemplate: null
                    },
                    data: []
                };
            }
            //create the drop down data source 
            for (let i = 0; i < this._storedTemplatesData.data.length; i++) {
                //update to new format
                this._updateNewFormatData(this._storedTemplatesData.data[i].items);

                this._templatesDS.push({
                    text: this._storedTemplatesData.data[i].text,
                    value: this._storedTemplatesData.data[i].name
                });
            }

            //refresh the drop down
            this._templatesRef.dataSource.read();
            //update the auto run checkbox

            this.setState({ autoSearch: this._storedTemplatesData.cfg.runOnSelect });
        }
    }

    private _getValuesForSave = (controlData: StringKeyDictionary<SearchFilterControl>): Array<SingleSearchElementData> => {
        const result: Array<SingleSearchElementData> = [];

        for (let rc in controlData) {
            const sc = controlData[rc];
            if (sc.control.IsRange && (sc.control.Type === SearchControlType.Date || sc.control.Type === SearchControlType.DateTime)) {
                if (Helper.hasValue(sc.value) && sc.value !== "") {
                    const item: SingleSearchElementData = {
                        colRef: `${rc}_start`,
                        val: sc.value
                    }
                    result.push(item);
                }
                if (Helper.hasValue(sc.rangeEndValue) && sc.rangeEndValue !== "") {
                    const item: SingleSearchElementData = {
                        colRef: `${rc}_end`,
                        val: sc.rangeEndValue
                    }
                    result.push(item);
                }
            } else {
                if (Helper.hasValue(sc.value) && sc.value !== "") {
                    const item: SingleSearchElementData = {
                        colRef: rc,
                        val: sc.value
                    }
                    result.push(item);
                }
            }
        }

        return result;
    }

    private _saveTemplate = () => {
        const currentFilterData = this.props.filterDataProvider();
        let canSave = true;

        //add new template
        if (this._currentTemplate === this._newTemplateOptionId) {
            if (Helper.hasValue(this.state.templateName) && this.state.templateName.trim() !== "") {
                const idx = Helper.findObjIndex(this._templatesDS, "value", this.state.templateName);
                if (idx >= 0) {
                    canSave = false;
                    NotificationHelper.showErrorMessage(this.props.translations["Suche.VorlageNameVergeben"]);
                } else {
                    const newTemplate: PersistedSearchTemplateInstance = {
                        items: [],
                        name: this.state.templateName,
                        text: this.state.templateName
                    }

                    newTemplate.items = this._getValuesForSave(this.props.referenceColumns);

                    this._storedTemplatesData.data.push(newTemplate);
                    this._templatesDS.push({
                        text: newTemplate.text,
                        value: newTemplate.name
                    });

                    this._templatesRef.dataSource.read();
                    this._templatesRef.select(this._templatesDS.length - 1);
                    this._templatesRef.trigger("change");
                }
            } else {
                canSave = false;
                NotificationHelper.showErrorMessage(this.props.translations["Suche.VorlageNameNichtNull"]);
            }
        } else {
            //update current template
            const idx = Helper.findObjIndex(this._storedTemplatesData.data, "name", this._currentTemplate);
            const templateInstance = this._storedTemplatesData.data[idx];
            templateInstance.items.length = 0;
            this._getValuesForSave(this.props.referenceColumns).forEach((item) => {
                templateInstance.items.push(item);
            });

            this._triggerTemplateChanged(templateInstance);
        }
        //save
        if (canSave) {
            this._persistTemplates();
        }
    }

    //delete selected template
    private _deleteTemplate = () => {
        const idx = Helper.findObjIndex(this._storedTemplatesData.data, "name", this._currentTemplate);
        this._storedTemplatesData.data.splice(idx, 1);
        const dsIdx = Helper.findObjIndex(this._templatesDS, "value", this._currentTemplate);
        this._templatesDS.splice(dsIdx, 1);
        this._templatesRef.dataSource.read();
        this._templatesRef.select(0);
        this._templatesRef.trigger("change");

        this._persistTemplates();
        Helper.removeDataFromStorage(this._STORAGE_KEY);
    }

    private _persistTemplates = async () => {
        this._storedTemplatesData.cfg.currentTemplate = this._currentTemplate;
        this._storedTemplatesData.cfg.runOnSelect = this.state.autoSearch;

        //remove entries with no value (send only controls with specified values)
        if (this._removeOldData) {
            if (Array.isArray(this._storedTemplatesData.data)) {
                this._storedTemplatesData.data.forEach((t, tIdx) => {
                    if (Array.isArray(t.items)) {
                        const indicesToRemove: Array<number> = [];
                        t.items.forEach((item, iIdx) => {
                            if (!Helper.hasValue(item.val) || item.val === "") {
                                indicesToRemove.push(iIdx);
                            }
                        });

                        for (let i = indicesToRemove.length - 1; i >= 0; i--) {
                            t.items.splice(indicesToRemove[i], 1);
                        };
                    }
                });
            }
        }

        const _ = await HTTPService.post("DynamicSearch/SaveSearchParameters", { searchType: this.props.searchType, data: JSON.stringify(this._storedTemplatesData) });
    }

    public resetTemplateNameSelect = () => {
        this._templatesRef.select(0);
    }

    assignDefaultTemplate = (template: PersistedSearchTemplateInstance) => {
        this.setState({ merge: this.props.initialSearchTemplate.merge }, () => {
            this._templatesRef.value(template.name)
            this._onTemplateChange(template.name);
        });
    }

    async componentDidMount() {
        //templates drop down list
        this._templatesRef = $(this._templatesElementRef).kendoDropDownList({
            dataTextField: "text",
            dataValueField: "value",
            dataSource: this._templatesDS,
            change: (e: kendo.ui.DropDownListChangeEvent) => {
                this.setState({ merge: false }, () => {
                    this._onTemplateChange(e.sender.value());
                });
            }
        }).data("kendoDropDownList");

        //get the templates from the server
        await this._loadSearchTemplates();
        if (Helper.hasValue(this.props.initialSearchTemplate.initialTemplate))
            this.assignDefaultTemplate(this.props.initialSearchTemplate.initialTemplate);
        this._initialLoad = false;
    }

    render() {
        return (
            <div className="advanced-search__search-templates">
                <label className="pointer">{this.props.translations["Suche.Suchvorlagen"]}</label>
                <input style={{ margin: "5px" }} ref={(r) => this._templatesElementRef = r} />
                <label style={{ margin: "7px", display: this._currentTemplate === this._newTemplateOptionId ? "inline" : "none" }}>
                    {this.props.translations["Suche.VorlageName"]}
                    <span className="k-input k-textbox k-input-solid k-input-md k-rounded-md" style={{ margin: "5px" }} >
                        <input type="text" className="k-input-inner" value={this.state.templateName} onChange={(e) => this._onNewNameChange(e.target.value)} />
                    </span>
                </label>
                <Button buttonStyle={{ margin: "5px", borderRadius: "var(--border-radius)" }} readOnly={!this._enableSaveButton} iconName={"check"} handleClick={() => this._onBtnClick(true)} />
                <Button buttonStyle={{ borderRadius: "var(--border-radius)" }} readOnly={!this._enableDeleteButton} iconName={"minus"} handleClick={() => this._onBtnClick(false)} />
                <label style={{ display: this.state.merge ? "block" : "none" }}>{Helper.getTranslation("Suche.Merge")}</label>
            </div>
        );
    }
}

interface PersistedSearchTemplates {
    cfg: {
        runOnSelect: boolean;
        currentTemplate: string;
    };
    data: Array<PersistedSearchTemplateInstance>;
}

export class SearchTemplatesHelper {
    public static getDefaultTemplate = (searchType: string) => {
        const key = `SEARCH_TEMPLATE_DEFAULT_${searchType}_${Config.user.userName}`;
        const defaultTemplate = Helper.loadDataFromStorage(key);
        const noTemplateOptionId = "*n*o**t*";
        const newTemplateOptionId = "*n*e*w**t*";
        if (Helper.isNullOrEmpty(defaultTemplate) || defaultTemplate == noTemplateOptionId || defaultTemplate == newTemplateOptionId)
            return null;
        return defaultTemplate;
    }

    public static mapPersitedSearchTemplateInstanceToData = (target: PersistedSearchTemplateInstance) => {
        const data = {};
        target.items.forEach((item) => {
            if (Array.isArray(item.val)) {
                data[item.colRef] = item.val.slice();
            } else {
                data[item.colRef] = item.val;
            }
        });
        return data;
    }
}

interface DynamicSearchTemplatesProps {
    translations: StringKeyDictionary<string>;
    onTemplateSelected: (templateData: any, triggerSearch: boolean, merge: boolean) => void;
    searchType: string;
    referenceColumns: StringKeyDictionary<SearchFilterControl>;//StringKeyDictionary<{ type: Innosoft.App.DynamicSearch.DynamicSearchControlType; id: string; }>;
    filterDataProvider: () => StringKeyDictionary<any>;
    initialSearchTemplate: InitialSearchTemplate;
}

export interface SearchFilterControl {
    value: any;
    rangeEndValue: any;
    control: DynamicSearchControl;
    //Nur für Textboxen wichtig
    //ich hasse unseren source -.-
    comparativeOperator?: 0 | 8 | 6 | 7;
}

export interface InitialSearchTemplate {
    initialTemplate: PersistedSearchTemplateInstance;
    merge: boolean;
}

interface PersistedSearchTemplateInstance {
    text: string;
    name: string;
    items: Array<SingleSearchElementData>;
}

export interface SingleSearchElementData {
    id?: string;
    val?: any;
    type?: string;
    colRef?: string;
    source?: string;
}